import { Injectable, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import {environment} from "../../../../environments/environment";
import {DebugLoggerService} from "./debug-logger.service";


export interface WebSocketEvent {
  event: string;
  data: unknown;
  timestamp: Date;
}

export interface QuoteUpdateEvent {
  quoteId?: number;
  exportId?: string;
  type: 'quote_updated' | 'quote_created' | 'quote_status_changed' | 'quote_product_updated' | 'sample_request_created';
  data: unknown;
  userId?: number;
  socketId?: string;
}

export interface SampleUpdateEvent {
  quoteId?: number;
  exportId?: string;
  type: 'sample_request_created';
  data: unknown;
  userId?: number;
  socketId?: string;
}

export interface OrderUpdateEvent {
  orderId?: number;
  exportId?: string;
  type: 'order_created' | 'order_updated';
  data: unknown;
  userId?: number;
  socketId?: string;
}

export interface TaskUpdateEvent {
  taskId?: number;
  type: 'task_refresh';
  data: unknown;
  userId?: number;
  socketId?: string;
}

/** Assembled stock figures may have moved (server-emitted to room 'assembled_stock'). */
export interface AssembledStockEvent {
  product_ids: number[];
  quote_id?: number;
}

/** Samples Fulfilment changed (api-v2, server-emitted): 'picks' = runs and pick counts only,
 *  'board' = the sample requests themselves too. */
export interface SampleFulfilmentEvent {
  scope: 'picks' | 'board';
  at: string;
}

export interface POSyncProgressEvent {
  jobId: string;
  type: 'new_po' | 'existing_po';
  status: 'running' | 'completed' | 'failed';
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  errors: string[];
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private socket: Socket | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly isBrowser: boolean;

  // Connection state management
  private connectionState$ = new BehaviorSubject<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second

  // Event streams
  private quoteEvents$ = new Subject<QuoteUpdateEvent>();
  private samplesEvents$ = new Subject<SampleUpdateEvent>();
  private orderEvents$ = new Subject<OrderUpdateEvent>();
  private taskEvents$ = new Subject<TaskUpdateEvent>();
  private poSyncEvents$ = new Subject<POSyncProgressEvent>();
  private assembledStockEvents$ = new Subject<AssembledStockEvent>();
  private sampleFulfilmentEvents$ = new Subject<SampleFulfilmentEvent>();
  private generalEvents$ = new Subject<WebSocketEvent>();
  private gmailEvents$ = new Subject<WebSocketEvent>();

  // Room management
  private currentRooms = new Set<string>();
  // Rooms requested while the socket was not yet connected; joined once connected
  private pendingRooms = new Set<string>();

  // Events requested while the socket was not yet connected; sent once connected
  private pendingEmits: Array<{ event: string; data: unknown }> = [];
  private readonly maxPendingEmits = 50;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private debugLogger: DebugLoggerService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.initializeConnection();
  }

  /**
   * Initialize WebSocket connection
   * Skips connection during server-side rendering
   */
  private initializeConnection(): any {
    // Skip WebSocket connection during server-side rendering
    if (!this.isBrowser) {
      this.debugLogger.log('Skipping WebSocket connection during SSR');
      return;
    }

    if (this.socket?.connected) {
      return;
    }

    this.connectionState$.next('connecting');

    const socketOptions: {
      transports: string[];
      timeout: number;
      forceNew: boolean;
      reconnection: boolean;
      reconnectionAttempts: number;
      reconnectionDelay: number;
    } = {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    };

    // Tear down any previous socket first — forceNew creates a fresh connection,
    // so keeping the old one around would leave a stale socket with duplicate
    // listeners emitting events alongside the new one.
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.socket = io(environment.socketUrl, socketOptions);
    this.setupEventListeners();
  }

  /**
   * Setup Socket.IO event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      this.debugLogger.log('WebSocket connected:', this.socket?.id);
      this.connectionState$.next('connected');
      this.reconnectAttempts = 0;
      this.rejoinRooms();
      this.flushPendingEmits();
    });

    this.socket.on('disconnect', (reason: string) => {
      this.debugLogger.log('WebSocket disconnected:', reason);
      this.connectionState$.next('disconnected');
      // Preserve joined rooms as pending so they are re-joined on reconnect
      this.currentRooms.forEach(room => this.pendingRooms.add(room));
      this.currentRooms.clear();
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('WebSocket connection error:', error);
      this.connectionState$.next('error');
      this.handleReconnection();
    });

    // Quote-specific events
    // ✅ No socket ID check - emit to all subscribers including same socket
    // This allows list pages to refresh even when updating from detail page in same browser
    this.socket.on('quote_updated', (data: QuoteUpdateEvent) => {
      this.debugLogger.log('🔔 quote_updated event received from server - emitting to all subscribers:', data);
      this.quoteEvents$.next(data);
    });

    // Sample-specific events
    this.socket.on('sample_request_created', (data: SampleUpdateEvent) => {
      if (this.socket?.id !== data.socketId) {
        this.debugLogger.log('sample request created event received:', data);
        this.samplesEvents$.next(data);
      }
    });

    this.socket.on('quote_created', (data: QuoteUpdateEvent) => {
      this.debugLogger.log('Quote created event received:', data);
      this.quoteEvents$.next(data);
    });

    // Order-specific events
    // ✅ No socket ID check - emit to all subscribers including same socket
    // This allows order list to refresh even when updating from order detail in same browser
    this.socket.on('order_created', (data: OrderUpdateEvent) => {
      this.debugLogger.log('🔔 order_created event received from server - emitting to all subscribers:', data);
      this.debugLogger.log('📍 Current socket ID:', this.socket?.id);
      this.debugLogger.log('📍 Event socket ID:', data.socketId);
      this.debugLogger.log('📍 Current rooms:', Array.from(this.currentRooms));
      this.orderEvents$.next(data);
    });

    // Task-specific events
    // ✅ No socket ID check - emit to all subscribers including same socket
    // This allows task list to refresh when tasks are updated
    this.socket.on('task_refresh', (data: TaskUpdateEvent) => {
      this.debugLogger.log('🔔 task_refresh event received from server - emitting to all subscribers:', data);
      this.taskEvents$.next(data);
    });

    // Gmail real-time events (server-emitted from PubSub handler)
    // 'email:full_sync' is the server's "reload the list" signal after a connect or a
    // history gap. It was missing from this list, so the only handler for it
    // (email-list.component.ts) never fired and the inbox stayed stale until a manual
    // refresh — the event was emitted and dropped here.
    ['email:new', 'email:updated', 'email:deleted', 'email:full_sync', 'email:sync_started', 'email:sync_completed', 'email:sync_error'].forEach((event) => {
      this.socket!.on(event, (data: any) => {
        this.gmailEvents$.next({ event, data, timestamp: new Date() });
      });
    });

    // PO Sync progress events (server-emitted, no socket ID check needed)
    this.socket.on('po_sync_progress', (data: POSyncProgressEvent) => {
      this.poSyncEvents$.next(data);
    });

    // An order reached or changed in Unleashed, or was cancelled: the assembled products listed
    // may now show different stock (server-emitted, no socket ID check needed)
    this.socket.on('assembled_stock_changed', (data: AssembledStockEvent) => {
      this.assembledStockEvents$.next(data);
    });

    // A pick, run, packed count or sample status changed: open Samples Fulfilment screens refresh
    // (server-emitted to the 'sample_fulfilment' room, coalesced; no socket ID check needed)
    this.socket.on('sample_fulfilment_changed', (data: SampleFulfilmentEvent) => {
      this.sampleFulfilmentEvents$.next(data);
    });

    // Room management events
    this.socket.on('room_joined', (data: { room: string }) => {
      this.debugLogger.log('Joined room:', data.room);
      this.currentRooms.add(data.room);
    });

    this.socket.on('room_left', (data: { room: string }) => {
      this.debugLogger.log('Left room:', data.room);
      this.currentRooms.delete(data.room);
    });
  }

  /**
   * Handle reconnection logic with exponential backoff
   */
  private handleReconnection(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    this.debugLogger.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.initializeConnection();
    }, delay);
  }

  /**
   * Rejoin all rooms after reconnection
   */
  private rejoinRooms(): void {
    // Join any rooms that were requested before the connection was established
    // (or that were active before a disconnect). Draining pendingRooms first
    // avoids the "already in room" short-circuit inside joinRoom().
    const rooms = new Set<string>([...this.currentRooms, ...this.pendingRooms]);
    this.pendingRooms.clear();
    this.currentRooms.clear();
    rooms.forEach(room => this.joinRoom(room));
  }

  /**
   * Join a specific room (quote-based or export-based)
   */
  public joinRoom(roomId: string): void {
    if (!this.socket?.connected) {
      // Connection isn't ready yet (Socket.IO connects asynchronously). Queue the
      // room so it is joined once the 'connect' event fires (see rejoinRooms),
      // instead of dropping the request and logging a warning.
      this.pendingRooms.add(roomId);
      this.debugLogger.log('WebSocket not connected yet; queued room join:', roomId);
      return;
    }

    // Check if already in the room to prevent duplicate joins
    if (this.currentRooms.has(roomId)) {
      this.debugLogger.log('Already in room:', roomId);
      return;
    }

    this.debugLogger.log('Joining room:', roomId);
    this.socket.emit('join_room', { room: roomId });
    this.currentRooms.add(roomId);
  }

  /**
   * Leave a specific room
   */
  public leaveRoom(roomId: string): void {
    // Drop it from the pending queue so it isn't re-joined once connected
    this.pendingRooms.delete(roomId);

    if (!this.socket?.connected) {
      this.debugLogger.log('WebSocket not connected; removed queued room:', roomId);
      return;
    }

    this.debugLogger.log('Leaving room:', roomId);
    this.socket.emit('leave_room', { room: roomId });
    this.currentRooms.delete(roomId);
  }

  /**
   * Get connection state observable
   */
  public getConnectionState(): Observable<'disconnected' | 'connecting' | 'connected' | 'error'> {
    return this.connectionState$.asObservable();
  }

  /**
   * Get quote events observable
   */
  public getQuoteEvents(): Observable<QuoteUpdateEvent> {
    return this.quoteEvents$.asObservable();
  }

  /**
   * Get sample events observable
   */
  public getSampleEvents(): Observable<SampleUpdateEvent> {
    return this.samplesEvents$.asObservable();
  }

  /**
   * Get order events observable
   */
  public getOrderEvents(): Observable<OrderUpdateEvent> {
    return this.orderEvents$.asObservable();
  }

  /**
   * Get task events observable
   */
  public getTaskEvents(): Observable<TaskUpdateEvent> {
    return this.taskEvents$.asObservable();
  }

  /**
   * Get PO sync progress events observable
   */
  public getPOSyncEvents(): Observable<POSyncProgressEvent> {
    return this.poSyncEvents$.asObservable();
  }

  /**
   * Get assembled stock change events observable (join room 'assembled_stock' to receive them)
   */
  public getAssembledStockEvents(): Observable<AssembledStockEvent> {
    return this.assembledStockEvents$.asObservable();
  }

  /** Samples Fulfilment refresh signals - join the 'sample_fulfilment' room to receive them. */
  public getSampleFulfilmentEvents(): Observable<SampleFulfilmentEvent> {
    return this.sampleFulfilmentEvents$.asObservable();
  }

  /**
   * Get general events observable
   */
  public getGeneralEvents(): Observable<WebSocketEvent> {
    return this.generalEvents$.asObservable();
  }

  public getGmailEvents(): Observable<WebSocketEvent> {
    return this.gmailEvents$.asObservable();
  }

  /**
   * Emit a custom event to the server
   */
  public emit(event: string, data: unknown): void {
    if (!this.isBrowser) {
      this.debugLogger.log('Skipping WebSocket emit during SSR:', event);
      return;
    }

    if (!this.socket?.connected) {
      // Socket.IO connects asynchronously and reconnects after a drop, so the
      // socket is routinely unavailable at the moment a component emits. Queue
      // the event and send it once connected instead of dropping it, which
      // would leave other clients without the refresh notification.
      this.queuePendingEmit(event, data);
      return;
    }

    this.sendEmit(event, data);
  }

  /**
   * Queue an event to be sent once the connection is established
   */
  private queuePendingEmit(event: string, data: unknown): void {
    // Bound the queue so a long outage can't grow it without limit
    if (this.pendingEmits.length >= this.maxPendingEmits) {
      const dropped = this.pendingEmits.shift();
      console.warn(`⚠️ Pending WebSocket emit queue full; dropped oldest event: ${dropped?.event}`);
    }

    this.pendingEmits.push({ event, data });
    this.debugLogger.log(`WebSocket not connected yet; queued "${event}" event:`, data);

    // Make sure a connection attempt is actually in flight (e.g. after an
    // explicit disconnect() the socket is null and nothing would reconnect)
    if (!this.socket) {
      this.initializeConnection();
    }
  }

  /**
   * Send all events queued while disconnected
   */
  private flushPendingEmits(): void {
    if (!this.pendingEmits.length) return;

    const queued = this.pendingEmits;
    this.pendingEmits = [];

    this.debugLogger.log(`📤 Flushing ${queued.length} queued WebSocket event(s)`);
    queued.forEach(({ event, data }) => this.sendEmit(event, data));
  }

  /**
   * Send an event on the live socket, stamping it with the current socket ID
   */
  private sendEmit(event: string, data: unknown): void {
    if (!this.socket?.connected) return;

    // Add socket ID to the data to identify the sender. Non-object payloads
    // (e.g. `true`) spread to an empty object, leaving just the socket ID.
    const dataWithSocketId = {
      ...data as object,
      socketId: this.socket.id
    };

    this.debugLogger.log(`📤 Emitting "${event}" event to server:`, dataWithSocketId);
    this.socket.emit(event, dataWithSocketId);
  }

  /**
   * Check if currently connected
   */
  public isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get current socket ID
   */
  public getSocketId(): string | undefined {
    return this.socket?.id;
  }

  /**
   * Get current rooms
   */
  public getCurrentRooms(): string[] {
    return Array.from(this.currentRooms);
  }

  /**
   * Manually reconnect
   */
  public reconnect(): void {
    if (!this.isBrowser) {
      this.debugLogger.log('Skipping WebSocket reconnect during SSR');
      return;
    }

    if (this.socket) {
      this.socket.disconnect();
    }
    this.reconnectAttempts = 0;
    this.initializeConnection();
  }

  /**
   * Disconnect WebSocket
   */
  public disconnect(): void {
    if (this.socket) {
      this.currentRooms.clear();
      this.pendingRooms.clear();
      // Drop queued events — an explicit disconnect means they are no longer wanted
      this.pendingEmits = [];
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.connectionState$.next('disconnected');
    }
  }

  /**
   * Cleanup on service destruction
   */
  public ngOnDestroy(): void {
    this.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
